import {
  Save,
  Send,
  ShieldCheck,
  UserCog,
  X,
} from "lucide-react";
import {
  AutocompleteSelect,
} from "../../shared/components/AutocompleteSelect";
import {
  ModuleHeader,
} from "../../shared/components/ModuleHeader";
import { useTeamWorkspace, type TeamModuleProps } from "./useTeamWorkspace";

export function TeamModule(props: TeamModuleProps) {
  const {
    members,
    composerOpen,
    setComposerOpen,
    inviteSubmitting,
    directoryQuery,
    setDirectoryQuery,
    directoryUsers,
    selectedDirectoryUserId,
    directorySearching,
    directorySearchFailed,
    editing,
    setEditing,
    editForm,
    setEditForm,
    form,
    setForm,
    selectDirectoryUser,
    inviteMember,
    openEditor,
    toggleRole,
    saveMember,
  } = useTeamWorkspace(props);
  return (
    <>
      <ModuleHeader
        eyebrow="Erişim yönetimi"
        title="Ekip ve Yetkiler"
        description="Kullanıcıların departman erişimini ve satın alma sürecindeki yetkilerini yönetin."
        action={
          <button
            className="primary-button"
            onClick={() => setComposerOpen(true)}
          >
            <UserCog size={16} /> Çalışan davet et
          </button>
        }
      />
      {composerOpen ? (
        <section
          className="inline-form-card"
          role="dialog"
          aria-modal="true"
          aria-label="Çalışan davet et"
        >
          <div className="section-heading compact">
            <div>
              <span className="section-kicker">Kurumsal erişim</span>
              <h2>Çalışan davet et</h2>
            </div>
            <button
              className="icon-button"
              aria-label="Davet formunu kapat"
              onClick={() => setComposerOpen(false)}
            >
              <X size={17} />
            </button>
          </div>
          <div className="inline-form-grid">
            <AutocompleteSelect
              className="form-field-wide"
              label="Şirket dizininde çalışan ara"
              value={selectedDirectoryUserId}
              options={directoryUsers.map((user) => ({
                value: user.id,
                label: user.name,
                description: [user.email, user.department, user.title]
                  .filter(Boolean)
                  .join(" · "),
                keywords: [user.email, user.department, user.title],
              }))}
              onChange={selectDirectoryUser}
              onQueryChange={setDirectoryQuery}
              placeholder="Ad veya kurumsal e-posta ile ara"
              loading={directorySearching}
              emptyMessage={
                directorySearchFailed
                  ? "Şirket dizinine ulaşılamadı. Bilgileri aşağıdan manuel girebilirsiniz."
                  : directoryQuery.trim().length < 2
                    ? "Aramak için en az iki karakter yazın."
                    : "Şirket dizininde eşleşen çalışan bulunamadı."
              }
            />
            <label>
              Ad soyad
              <input
                value={form.name}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    name: event.target.value,
                  }))
                }
                placeholder="Örn. Ayşe Kara"
              />
            </label>
            <label>
              Kurumsal e-posta
              <input
                type="email"
                value={form.email}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    email: event.target.value,
                  }))
                }
                placeholder="ayse.kara@anlgida.com.tr"
              />
            </label>
            <label>
              Departman
              <select
                value={form.department}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    department: event.target.value,
                  }))
                }
              >
                <option>Planlama</option>
                <option>Üretim</option>
                <option>Lojistik</option>
                <option>Teknik</option>
                <option>Satın Alma</option>
                <option>Kalite</option>
                <option>Bilgi Teknolojileri</option>
                <option>Genel Müdürlük</option>
              </select>
            </label>
            <label>
              Yetki rolü
              <select
                value={form.role}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    role: event.target.value,
                  }))
                }
              >
                <option value="PLANLAMA">Planlama</option>
                <option value="SATIN_ALMA">Satın Alma Uzmanı</option>
                <option value="SATIN_ALMA_MUDURU">Satın Alma Müdürü</option>
                <option value="GENEL_MUDUR">Genel Müdür</option>
                <option value="KALITE_UZMANI">Kalite Uzmanı</option>
                <option value="KALITE_MUDURU">Kalite Müdürü</option>
                <option value="URETIM_OPERATORU">Üretim Operatörü</option>
                <option value="VARDIYA_AMIRI">Vardiya Amiri</option>
                <option value="URETIM_YONETICISI">Üretim Yöneticisi</option>
                <option value="TEKNIK_MUDURU">Teknik Departman Müdürü</option>
                <option value="DEPO_GOREVLISI">Depo Görevlisi</option>
                <option value="CEP_DEPO_SORUMLUSU">Cep Depo Sorumlusu</option>
                <option value="SISTEM_YONETICISI">Sistem Yöneticisi</option>
              </select>
            </label>
          </div>
          <div className="inline-form-actions">
            <button
              className="secondary-button"
              onClick={() => setComposerOpen(false)}
            >
              Vazgeç
            </button>
            <button
              className="approve-button"
              disabled={inviteSubmitting}
              onClick={inviteMember}
            >
              <Send size={15} />
              {inviteSubmitting ? "Davet gönderiliyor…" : "Daveti gönder"}
            </button>
          </div>
        </section>
      ) : null}
      {editing ? (
        <section
          className="inline-form-card"
          role="dialog"
          aria-modal="true"
          aria-label="Kullanıcı erişimini düzenle"
        >
          <div className="section-heading compact">
            <div>
              <span className="section-kicker">Kullanıcı detayı</span>
              <h2>{editing.email}</h2>
            </div>
            <button
              className="icon-button"
              aria-label="Kullanıcı düzenlemeyi kapat"
              onClick={() => setEditing(null)}
            >
              <X size={17} />
            </button>
          </div>
          <div className="inline-form-grid">
            <label>
              Ad soyad
              <input
                value={editForm.name}
                onChange={(event) =>
                  setEditForm((current) => ({
                    ...current,
                    name: event.target.value,
                  }))
                }
              />
            </label>
            <label>
              Unvan
              <input
                value={editForm.title}
                onChange={(event) =>
                  setEditForm((current) => ({
                    ...current,
                    title: event.target.value,
                  }))
                }
              />
            </label>
            <label>
              Departman
              <select
                value={editForm.department}
                onChange={(event) =>
                  setEditForm((current) => ({
                    ...current,
                    department: event.target.value,
                  }))
                }
              >
                {[
                  "Planlama",
                  "Üretim",
                  "Lojistik",
                  "Teknik",
                  "Satın Alma",
                  "Genel Müdürlük",
                  "Bilgi Teknolojileri",
                ].map((department) => (
                  <option key={department}>{department}</option>
                ))}
              </select>
            </label>
            <label>
              Hesap durumu
              <select
                value={editForm.status}
                onChange={(event) =>
                  setEditForm((current) => ({
                    ...current,
                    status: event.target.value,
                  }))
                }
              >
                <option value="ACTIVE">Aktif</option>
                <option value="PENDING">İlk giriş bekleniyor</option>
                <option value="PASSIVE">Pasif</option>
              </select>
            </label>
            <fieldset className="role-picker form-field-wide">
              <legend>Roller</legend>
              {[
                ["PLANLAMA", "Planlama"],
                ["SATIN_ALMA", "Satın Alma"],
                ["SATIN_ALMA_MUDURU", "Satın Alma Müdürü"],
                ["GENEL_MUDUR", "Genel Müdür"],
                ["URETIM_OPERATORU", "Üretim Operatörü"],
                ["VARDIYA_AMIRI", "Vardiya Amiri"],
                ["URETIM_YONETICISI", "Üretim Yöneticisi"],
                ["TEKNIK_MUDURU", "Teknik Departman Müdürü"],
                ["SISTEM_YONETICISI", "Sistem Yöneticisi"],
              ].map(([code, label]) => (
                <label key={code}>
                  <input
                    type="checkbox"
                    checked={editForm.roles.includes(code)}
                    onChange={() => toggleRole(code)}
                  />
                  {label}
                </label>
              ))}
            </fieldset>
          </div>
          <div className="inline-form-actions">
            <button
              className="secondary-button"
              onClick={() => setEditing(null)}
            >
              Vazgeç
            </button>
            <button
              className="approve-button"
              disabled={!editForm.roles.length}
              onClick={saveMember}
            >
              <Save size={15} /> Erişimi güncelle
            </button>
          </div>
        </section>
      ) : null}
      <section className="module-card">
        <div className="data-table-wrap">
          <table className="data-table team-table">
            <thead>
              <tr>
                <th>Kullanıcı</th>
                <th>Departman</th>
                <th>Rol</th>
                <th>Veri erişimi</th>
                <th>Durum</th>
                <th aria-label="İşlemler" />
              </tr>
            </thead>
            <tbody>
              {members.map((member) => (
                <tr key={member.name}>
                  <td>
                    <div className="team-member">
                      <span>{member.initials}</span>
                      <strong>{member.name}</strong>
                    </div>
                  </td>
                  <td>{member.department}</td>
                  <td>{member.role}</td>
                  <td>{member.access}</td>
                  <td>
                    <span
                      className={`plain-status ${member.status === "Aktif" ? "plain-önerilen" : "plain-bilgi-eksik"}`}
                    >
                      {member.status}
                    </span>
                  </td>
                  <td>
                    <button
                      className="row-action"
                      onClick={() => openEditor(member)}
                    >
                      Düzenle
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      <section className="permission-note">
        <ShieldCheck size={18} />
        <div>
          <strong>En az yetki ilkesi</strong>
          <p>
            Kullanıcılar yalnızca görevleri için gerekli departman ve işlem
            kapsamına erişir.
          </p>
        </div>
      </section>
    </>
  );
}
